/* eslint-disable no-magic-numbers */
import { Colors } from '@futrli/colors';
import { createStyles, makeStyles } from '@mui/styles';
import { omit } from 'lodash';
import React, { forwardRef, useState } from 'react';
import { UIMenu, UIMenuProps } from './ui-menu';
import { Theme } from '@emotion/react';
import { alpha, MenuItem, MenuItemProps } from '@mui/material';

export interface UIMenuItemOwnProps {
  /**
   * Allows the menu item to have sub menus.
   */
  SubMenuProps?: Partial<UIMenuProps>;
  /**
   * Defines the purpose of the menu item.
   * @default 'forMenu'
   */
  variant?: 'forMenu' | 'forSelect';
  /**
   * Allows to use the component props from the MUI Menu Item.
   * @see issue https://github.com/mui-org/material-ui/issues/21210
   * @default li
   * @see https://material-ui.com/api/menu-item/
   */
  component?: React.ElementType;
}

export type UIMenuItemProps = UIMenuItemOwnProps & Omit<MenuItemProps, keyof UIMenuItemOwnProps>;

const getPalette = (_theme: Theme, _props?: UIMenuProps) => {
  return {
    boxShadow: alpha(Colors.tealGrey.lighten10, 0.2),
    border: Colors.tealGrey.lighten40,
    fontLight: Colors.tealGrey.lighten5,
    fontDark: Colors.tealGrey.darken20
  };
};

const useStyles = makeStyles<Theme, UIMenuItemProps>(theme => {
  const palette = getPalette(theme);

  return createStyles({
    root: props => {
      const cssRules = {
        'padding': '8px 16px',
        'color': palette.fontLight,
        'backgroundColor': 'transparent',
        '&:hover': {
          color: palette.fontDark,
          backgroundColor: 'transparent'
        },
        '&$selected': {
          'color': palette.fontDark,
          'backgroundColor': 'transparent',
          '&:hover': {
            backgroundColor: 'transparent'
          }
        },
        '&$gutters': {
          'color': palette.fontLight,
          'backgroundColor': 'transparent',
          '&:hover': {
            color: palette.fontDark,
            backgroundColor: 'transparent'
          }
        },
        '& .MuiButtonBase-root': {
          // If the item has button icons
          margin: 0,
          marginRight: '12px'
        }
      };

      if (props.variant === 'forSelect') {
        Object.assign(cssRules, {
          '& span + svg': {
            fontSize: '20px',
            marginLeft: 'auto'
          }
        });
      }

      return cssRules;
    },
    selected: () => ({}),
    gutters: () => ({})
  });
});

export const UIMenuItem = forwardRef<HTMLLIElement, UIMenuItemProps>((props, ref) => {
  const { children, disableGutters = true, variant = 'forMenu', component = 'li', SubMenuProps, ...rest } = props;

  const uiClasses = useStyles(props);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const muiProps: MenuItemProps = omit(rest, ['SubMenuProps', 'variant']);
  // const asButton = button === false ? undefined : true;

  const handleMenuItemClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setAnchorEl(event.currentTarget);

    props.onClick?.(event as React.MouseEvent<HTMLLIElement, MouseEvent>);
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setAnchorEl(null);

    if (SubMenuProps?.onClose) {
      SubMenuProps.onClose(event, 'escapeKeyDown');
    }
  };

  if (!SubMenuProps || variant === 'forSelect') {
    return (
      <MenuItem
        ref={ref}
        {...muiProps}
        component={component}
        // button={asButton}
        disableGutters={disableGutters}
        classes={uiClasses}
      >
        {children}
      </MenuItem>
    );
  }

  return (
    <React.Fragment>
      <MenuItem
        ref={ref}
        {...muiProps}
        component={component}
        // button={asButton}
        disableGutters={disableGutters}
        onClick={handleMenuItemClick}
        classes={uiClasses}
      >
        {children}
      </MenuItem>
      <UIMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={handleClose}
        {...SubMenuProps}
      />
    </React.Fragment>
  );
});
